/**
 * Pure rules for the Regulatory reports page: when a report can be requested, and how the
 * API's codes and exact decimal strings are worded for staff. Nothing here parses money:
 * amounts and shares stay the strings the API sent.
 */
import type {
  AccreditationStatus,
  ResidenceBasis,
} from '../../services/regulatoryReports.service';

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const DASH = '—';

/** True for a real calendar day written YYYY-MM-DD. */
export function isRealDay(value: string): boolean {
  if (!DAY.test(value)) return false;
  const t = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(t.getTime()) && t.toISOString().slice(0, 10) === value;
}

export interface VehicleReportInput {
  vehicleId: string;
  asOf: string;
  busy: boolean;
}

/** The reason a per-vehicle report cannot be requested yet, worded for the screen. */
export function vehicleReportBlockedReason(input: VehicleReportInput): string | null {
  if (input.busy) return 'Working on a report…';
  if (!input.vehicleId) return 'Choose a vehicle first.';
  if (input.asOf.trim() && !isRealDay(input.asOf.trim())) {
    return 'Enter the as-of day as a real date, or leave it empty for today.';
  }
  return null;
}

/** The reason a vehicle-independent report cannot be requested right now. */
export function filingReportBlockedReason(busy: boolean): string | null {
  return busy ? 'Working on a report…' : null;
}

/** `MXN 1500.00; USD 250.50` — currencies in code order, amounts exactly as sent. */
export function moneyMapText(map: Record<string, string> | null | undefined): string {
  const entries = Object.entries(map ?? {}).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) return DASH;
  return entries.map(([ccy, amount]) => `${ccy} ${amount}`).join('; ');
}

export function accreditationLabel(status: AccreditationStatus): string {
  switch (status) {
    case 'current':
      return 'Current';
    case 'expired':
      return 'Expired';
    default:
      return 'None on record';
  }
}

export function residenceBasisLabel(basis: ResidenceBasis | 'as_of' | 'first_known'): string {
  switch (basis) {
    case 'as_of':
      return 'Known on the as-of day';
    case 'first_known':
      return 'First value recorded later';
    default:
      return 'Not recorded';
  }
}

/** `US / US-NY`, `MX`, or a dash when neither is recorded. */
export function residenceText(country: string | null, state: string | null): string {
  const parts = [country, state].filter((p): p is string => Boolean(p));
  return parts.length ? parts.join(' / ') : DASH;
}

/** The API names evidence as `interaction:<kind>`, `invitation_accepted` or `onboarding_started`. */
export function relationshipEvidenceLabel(evidence: string | null): string {
  if (!evidence) return 'None on record';
  if (evidence.startsWith('interaction:')) {
    const kind = evidence.slice('interaction:'.length).replace(/_/g, ' ');
    return kind ? `Staff contact (${kind})` : 'Staff contact';
  }
  if (evidence === 'invitation_accepted') return 'Invitation accepted';
  if (evidence === 'onboarding_started') return 'Onboarding started';
  return evidence.replace(/_/g, ' ');
}

export function yesNo(value: boolean): string {
  return value ? 'Yes' : 'No';
}

/** The state shown for a notice row; the API's unknown bucket is named, never hidden. */
export function stateLabel(code: string, unknownCode: string): string {
  return code === unknownCode ? `${code} (state not recorded)` : code;
}

/** A vehicle's name by id, else the id itself so a row is never unnamed. */
export function vehicleNameFor(
  productId: string,
  vehicles: ReadonlyArray<{ id: string; name: string }>,
): string {
  return vehicles.find((v) => v.id === productId)?.name ?? productId;
}
